import type { ForwardedRef, ReactNode } from 'react';
import type { InternalUserGuest, InternalUserUnverified } from '@ricoh/ria.client.core/model';

import { forwardRef, memo } from 'react';
import { matchPath, Navigate, Outlet, useLocation } from 'react-router';
import LoaderOverlay from '@ricoh/ria.client.core/components/indicator/loader/overlay';
import { Z_INDEX } from '@ricoh/ria.client.core/constants/ui/layout';
import { ServiceError } from '@ricoh/ria.client.core/errors';
import { isType } from '@ricoh/ria.client.core/utils/data/object';

import { useErrorLink } from '~/plugins/router/hooks/error';

import type { AuthProviderProps, AuthProviderRef } from './_.type';

import authConfig from '../_config';
import authenticator from '../_plugin';
import useAuthProviderHooks from './_.hook';

/** AuthProvider. */
export default memo(
  forwardRef(function AuthProvider(props: AuthProviderProps, ref: ForwardedRef<AuthProviderRef>): ReactNode {
    const location = useLocation();
    const errorLink = useErrorLink();

    const { states } = useAuthProviderHooks(props, ref);

    // * Error
    if (states.authorize.error) {
      if (matchPath(authConfig.redirect.error, location.pathname)) {
        return <Outlet />;
      }

      return (
        <Navigate
          to={authConfig.redirect.error}
          state={states.authorize.error}
          replace
        />
      );
    }

    // * Loading
    if (states.authorize.isMutating || !states.authorize.data) {
      return (
        <LoaderOverlay
          opacity={1}
          zIndex={Z_INDEX.MAX}
        />
      );
    }

    // * Verify Access

    // ** Unverified User -> Render
    if (
      authConfig.routes.unverifiedUser.some((route) => matchPath(route, location.pathname)) &&
      location.state &&
      typeof location.state === 'object' &&
      'user' in location.state &&
      isType<InternalUserUnverified>(location.state.user, 'unverifiedUser')
    ) {
      return <Outlet />;
    }

    // ** Access success to route -> Render
    if (states.authorize.data.routes.some((route) => matchPath(route, location.pathname))) {
      return <Outlet />;
    }

    // ** Access denied to route, meed authentication -> Redirect to signin
    if (isType<InternalUserGuest>(states.authorize.data.user, 'guest')) {
      // If signin page -> Render
      if (matchPath(authConfig.redirect.signin, location.pathname)) {
        return <Outlet />;
      }

      // Not signin page -> Redirect to signin
      return authenticator.redirectSignin(globalThis.location.href);
    }

    // ** Authenticated but access denied -> Redirect to default page
    // If no default page -> Redirect to default route
    if (!states.authorize.data.defaultPath) {
      return <Navigate {...errorLink(ServiceError.forbidden()).props} />;
    }

    // If default page -> Render
    if (matchPath(states.authorize.data.defaultPath, location.pathname)) {
      return <Outlet />;
    }

    // Not default page -> Redirect to default
    return (
      <Navigate
        to={states.authorize.data.defaultPath}
        replace
      />
    );
  }),
);
